<template>
  <transition name="modal-fade">
    <div v-if="visible" class="modal-overlay" role="dialog" aria-modal="true" tabindex="-1" :aria-label="$t('entityChoice.title')">
      <div class="modal-card">
        <div class="modal-lang-select">
          <select
            :value="$i18n.locale"
            :aria-label="$t('app.changeLanguage')"
            @change="changeLanguage($event.target.value)"
          >
            <option
              v-for="lang in languages"
              :key="lang.code"
              :value="lang.code"
            >
              {{ lang.title }}
            </option>
          </select>
        </div>
        <h2 class="modal-title">{{ $t('entityChoice.title') }}</h2>
        <p class="modal-subtitle">{{ $t('entityChoice.subtitle') }}</p>
        <div class="choice-grid">
          <button class="choice-option" @click="choose(false)">
            <i class="fas fa-building choice-icon" aria-hidden="true"></i>
            <span class="choice-label">{{ $t('entityChoice.commercial') }}</span>
            <span class="choice-desc">{{ $t('entityChoice.commercialDesc') }}</span>
          </button>
          <button class="choice-option" @click="choose(true)">
            <i class="fas fa-heart choice-icon" aria-hidden="true"></i>
            <span class="choice-label">{{ $t('entityChoice.charity') }}</span>
            <span class="choice-desc">{{ $t('entityChoice.charityDesc') }}</span>
          </button>
        </div>
        <button type="button" class="terms-link" @click="showTerms = true">
          {{ $t('entityChoice.viewTerms') }}
        </button>
        <a :href="mainWebsiteUrl" class="back-link">
          <i class="fas fa-arrow-left" aria-hidden="true"></i>
          {{ $t('entityChoice.backToMain') }}
        </a>
      </div>
      <terms-modal
        v-if="showTerms"
        :read-only="true"
        @cancel="showTerms = false"
      />
    </div>
  </transition>
</template>

<script>
import TermsModal from '@/components/checkout/terms-modal.vue'
import { languages, loadLocale } from '@/i18n'

const STORAGE_KEY = 'commercial_entity_type_chosen'

export default {
  name: 'EntityChoiceModal',
  components: {
    TermsModal
  },
  props: {
    mainWebsiteUrl: {
      type: String,
      default: 'https://www.marathon-photos.com'
    }
  },
  data() {
    return {
      languages,
      visible: false,
      showTerms: false
    }
  },
  mounted() {
    if (!sessionStorage.getItem(STORAGE_KEY)) {
      this.visible = true
      this.$nextTick(() => this.$el.focus())
    }
  },
  methods: {
    /**
     * Handle entity type selection, persist choice, and close modal.
     * @param {boolean} isCharity - Whether the user selected charity
     */
    choose(isCharity) {
      this.$store.dispatch('setIsCharity', isCharity)
      sessionStorage.setItem(STORAGE_KEY, isCharity ? 'charity' : 'commercial')
      this.visible = false
    },
    /**
     * Switch locale from the modal's language select.
     * @param {string} code - ISO language code
     */
    async changeLanguage(code) {
      await loadLocale(code)
      document.documentElement.lang = code
      localStorage.setItem('mp-locale', code)
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 1rem;
}

.modal-card {
  background: #fff;
  border-radius: 1rem;
  padding: 2.5rem 2rem 2rem;
  max-width: 520px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  position: relative;
}

.modal-lang-select {
  position: absolute;
  top: 0.75rem;
  right: 2rem;
  text-align: right;
}

.modal-lang-select select {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  color: var(--gray-900);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.2rem 0.9rem 0.2rem 0.25rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23374151'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0 center;
}

.modal-lang-select select:focus {
  outline: 1px solid var(--gray-300);
  border-radius: 0.2rem;
}

.modal-title {
  color: var(--mp-blue);
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
}

.modal-subtitle {
  color: var(--gray-600);
  font-size: 0.95rem;
  margin: 0 0 1.75rem;
}

.choice-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.choice-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  border: 2px solid var(--gray-200);
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
}

.choice-option:hover {
  border-color: var(--mp-blue);
  box-shadow: 0 4px 12px rgba(44, 0, 133, 0.15);
  transform: translateY(-2px);
}

.choice-option:focus {
  outline: 2px solid var(--mp-blue);
  outline-offset: 2px;
}

.choice-icon {
  font-size: 2rem;
  color: var(--mp-blue);
}

.choice-label {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--gray-900);
}

.choice-desc {
  font-size: 0.8rem;
  color: var(--gray-500);
  line-height: 1.3;
}

.terms-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--mp-blue);
  font-size: 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  margin: 0 auto 0.75rem;
}

.terms-link:hover {
  color: var(--bg-blue);
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: var(--gray-500);
  font-size: 0.9rem;
  text-decoration: none;
}

.back-link:hover {
  color: var(--mp-blue);
  text-decoration: underline;
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-active .modal-card,
.modal-fade-leave-active .modal-card {
  transition: transform 0.3s, opacity 0.3s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .modal-card,
.modal-fade-leave-to .modal-card {
  transform: scale(0.95);
  opacity: 0;
}

@media (max-width: 480px) {
  .modal-card {
    padding: 1.75rem 1.25rem 1.5rem;
  }

  .choice-grid {
    grid-template-columns: 1fr;
  }

  .choice-option {
    padding: 1.25rem 1rem;
  }
}
</style>
